import React, { useEffect, useState } from 'react';
import ProcessButton from '../../ProcessButton';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getUserDetails, getUserOrgsString } from '../../../store/user/userSlice';
import {
	callAPISync,
	createUploadHeader,
	getKeyphraseFiles,
	logProcessingStarted,
	publishMessage,
	sendSubscribeMessage,
} from '../../../utils/api';
import Logger from '../../../utils/logger';
import { getUploadData, resetFileUpload } from '../../../store/upload/uploadSlice';
import { beginProcessing } from '../../../store/processing/processingSlice';
import { Processors } from '../../../types/generic';
import { SubscriptionOptions } from '../../../utils/processorTopics';
import { KeyphrasesInfo, StellarCellebriteXMLMessage } from '../../../types/upload';
import { useLogto } from '@logto/react';
import { KeyphraseSelect } from '../../KeyphraseSelect';
import { UploadFormStep } from '../../Tabs/Upload/UploadTabSkeleton/stepWrapper';
import { useUploadFormStepper } from '../../Tabs/Upload/UploadFormContext';

const StellarCellebriteXMLMetadata: React.FC<{ index?: number }> = ({ index: baseIndex = 0 }) => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const userDetails = useAppSelector((state) => getUserDetails(state));
	const forceDetails = useAppSelector((state) => state.system.forceDetails);
	const userTeams = useAppSelector((state) => getUserOrgsString(state));
	const notifyOnComplete = useAppSelector((state) => state.processing.notifyOnComplete);

	const {
		fileName,
		fileOperation,
		deviceOwner,
		deviceOwnerType,
		suspectKnown,
		fileId,
		uploadReason,
		duplicatePreference,
		useCase,
		enrichments,
	} = useAppSelector((state) => getUploadData(state));

	const [publishError, setPublishError] = useState<string>('');
	const [keyphraseOptions, setKeyphraseOptions] = useState<KeyphrasesInfo[]>([]);
	const [selectedKeyphrase, setSelectedKeyphrase] = useState<KeyphrasesInfo>();
	const [keyphraseError, setKeyphraseError] = useState<string>('');
	const [loadingKeyphrases, setLoadingKeyphrases] = useState<boolean>(true);

	const onSubmit = () => {
		if (selectedKeyphrase) {
			const stellarMessage: StellarCellebriteXMLMessage = {
				...createUploadHeader(deviceOwner, fileName, userDetails, fileOperation, uploadReason, forceDetails, useCase, deviceOwnerType),
				metadata: {
					keywordsInfo: selectedKeyphrase,
					testVar: 'Hello',
				},
			};

			callAPISync(logto, (tokens) => {
				logProcessingStarted(
					tokens,
					fileOperation,
					fileName,
					uploadReason,
					fileId,
					userDetails.name,
					userTeams,
					userDetails.email,
					suspectKnown,
					selectedKeyphrase.location,
					selectedKeyphrase.lastModified,
					notifyOnComplete
				);
				sendSubscribeMessage(SubscriptionOptions.STELLAR_CELLEBRITE_XML, tokens);
				publishMessage(stellarMessage, 'upload.stellar.cellebrite.xml', fileId, tokens, duplicatePreference)
					.then(async (res) => {
						const txt = await res.text();
						if (res.ok) {
							Logger.debug('Submitted:\n', txt);
							dispatch(
								beginProcessing({
									fileName,
									fileId,
									operation: fileOperation,
									processor: Processors.STELLAR_CELLEBRITE,
									duplicatePreference,
									enrichments,
								})
							);
							dispatch(resetFileUpload());
						} else {
							Logger.error('Error submitting');
							Logger.error(res.status, res.statusText);
							setPublishError(res.statusText);
						}
					})
					.catch((e) => {
						Logger.error(e);
						setPublishError(JSON.stringify(e));
					});
			});
		} else {
			setKeyphraseError('A keyphrases file must be selected to begin processing. ');
		}
	};

	useEffect(() => {
		callAPISync(logto, (token) => {
			setLoadingKeyphrases(true);

			getKeyphraseFiles(token, 'stellar-cellebrite-xml')
				.then((res) => {
					if (res.ok) {
						return res.json();
					} else {
						throw new Error(`${res.status} ${res.statusText}`);
					}
				})
				.then((resp: KeyphrasesInfo[]) => {
					setKeyphraseOptions(resp);
					setLoadingKeyphrases(false);
				})
				.catch((e) => {
					setKeyphraseError(`An error occurred when fetching the keyphrases list (${String(e)})! `);
					setLoadingKeyphrases(false);
					Logger.error(e);
				});
		});
	}, []);

	const steps = [
		<UploadFormStep
			key="keyphrasesStellarCellebriteXML"
			label="Select a Keyphrase List"
			valid={Boolean(selectedKeyphrase)}
			validLabel={`Keyphrase List Selected (${selectedKeyphrase?.title})`}
			errorMessage="A keyphrase list must be selected"
		>
			<KeyphraseSelect
				selectedList={selectedKeyphrase}
				setSelectedList={(e) => setSelectedKeyphrase(e)}
				errorText={keyphraseError}
				keyphraseOptions={keyphraseOptions}
				helperText={'Choose the keyphrases list to be used when processing the file:'}
				isLoading={loadingKeyphrases}
			/>
		</UploadFormStep>,
		<UploadFormStep key="stellarCellebriteProcess" label="Final Confirmation" valid={true} validLabel="">
			<ProcessButton
				operation={fileOperation}
				onClick={onSubmit}
				pipelineName="Stellar Cellebrite"
				publishError={publishError}
				setPublishError={setPublishError}
			/>
		</UploadFormStep>,
	];

	const { activeStep } = useUploadFormStepper();

	return (
		<>
			{steps.map((step, i) => {
				const stepIndex = baseIndex + i;

				return React.cloneElement(step, {
					index: stepIndex,
					active: activeStep === stepIndex,
					completed: activeStep > stepIndex,
				});
			})}
		</>
	);
};

export default StellarCellebriteXMLMetadata;
